import { createAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { serializeThunkApiError } from "src/utils/helpers/rtkSerializeThunkApiError";
import { captureMessage } from '@sentry/browser';
import { t } from "i18next";
import axios from '../../config/axios';
import { globalConfig } from "../../config/axios-retry";
import Toast from "../../shared/toast/Toast";
import { AcceptInvitationRequest, EnterpriseStoreInferface, InviteUserRequest, JobTemplatesFilters, LocationInterface, LocationsFilters, NewLocationRequest, PostingJobItem, UpdateInvitationRequest, UpdateLocationRequest, UpdateLocationsForJobRequest, UpdateUserRequest, UserItem, UsersFilters } from "src/utils/interfaces/enterprise/EnterpriseInterfaces";
import { ENTERPRISE_LOCATIONS_PAGE_LIMIT, ENTERPRISE_USERS_PAGE_LIMIT, JOB_TEMPLATES_PAGE_LIMIT } from "src/utils/constants/constants";
import { PartnerUserStatusEnum, RoleEnum } from "src/utils/enums/EnterpriseEnums";
import { FullTimeJob } from "src/utils/interfaces/job/Job";
import { NewJobPost } from "src/utils/interfaces/job/NewJobPost";

const appKey = 'appEnterprise'
const usersApiModuleUrl = 'pa/ft/partner-users';
const jobsApiModuleUrl = 'pa/ft/job-posts';
const apiLocationsUrl = 'pa/ft/partner-locations';

export const getJobTemplates = createAsyncThunk(`${appKey}/getJobTemplates`, async (data: JobTemplatesFilters) => {
    const response = await axios.get(`${jobsApiModuleUrl}/job-templates/list`, { ...globalConfig, params: { skip: data.skip, limit: JOB_TEMPLATES_PAGE_LIMIT } });
    return response.data.data;
})

export const getJobTemplateDetails = createAsyncThunk(`${appKey}/getJobTemplateDetails`, async (id: string) => {
    const response = await axios.get(`${jobsApiModuleUrl}/job-templates/list`, { ...globalConfig, params: { skip: 0, limit: 50 } });
    return response.data?.data?.templates?.find(t => t.externalId === id);
})

export const addJobTemplate = createAsyncThunk(
    `${appKey}/addJobTemplate`,
    async (templateData: NewJobPost) => {
        const response = await axios.post(`${jobsApiModuleUrl}/job-templates/create`, templateData);
        return response.data?.data;
    },
    { serializeError: serializeThunkApiError }
);

export const updateJobTemplate = createAsyncThunk(
    `${appKey}/updateJobTemplate`,
    async (templateData: NewJobPost) => {
        const response = await axios.put(`${jobsApiModuleUrl}/job-templates/${templateData.jobId}`, templateData);
        return response.data?.data;
    },
    { serializeError: serializeThunkApiError }
);

export const deleteJobTemplate = createAsyncThunk(`${appKey}/deleteJobTemplate`, async (jobId: string) => {
    const response = await axios.delete(`${jobsApiModuleUrl}/job-templates/${jobId}`);
    return response.data?.data;
});


export const listUsers = createAsyncThunk(`${appKey}/listUsers`, async (data: UsersFilters) => {
    const params = { ...data };
    if (!params.searchKeyword) delete params.searchKeyword;
    delete params.usersOnly; delete params.invitedOnly;

    const response = await axios.get(`${usersApiModuleUrl}/user/list`, { ...globalConfig, params });
    return response.data.data;
});

export const updateUser = createAsyncThunk(`${appKey}/updateUser`, async (updateUserReq: UpdateUserRequest) => {
    const response = await axios.put(`${usersApiModuleUrl}/user/update/${updateUserReq.userId}`, { role: updateUserReq.role });
    return response.data?.data;
});

export const removeUser = createAsyncThunk(`${appKey}/removeUser`, async (userId: string) => {
    const response = await axios.delete(`${usersApiModuleUrl}/user/remove/${userId}`,);
    return response.data?.data;
});

export const listInvitations = createAsyncThunk(`${appKey}/listInvitations`, async (data: UsersFilters) => {
    const params = { ...data };
    if (!params.searchKeyword) delete params.searchKeyword;
    delete params.usersOnly; delete params.invitedOnly;

    const response = await axios.get(`${usersApiModuleUrl}/invitation/list`, { ...globalConfig, params });
    return response.data?.data;
});

export const sendInvitation = createAsyncThunk(`${appKey}/sendInvitation`, async (inviteUserReq: InviteUserRequest) => {
    const response = await axios.post(`${usersApiModuleUrl}/invitation/create`, { ...inviteUserReq });
    return response.data?.data;
});

export const getInvitationDetails = createAsyncThunk(`${appKey}/getInvitationDetails`, async (token: string) => {
    const response = await axios.get(`${usersApiModuleUrl}/invitation/details/${token}`, { ...globalConfig });
    return response.data?.data;
});


export const updateInvitation = createAsyncThunk(`${appKey}/updateInvitation`, async (updateInvitationReq: UpdateInvitationRequest) => {
    const response = await axios.put(`${usersApiModuleUrl}/invitation/update/${updateInvitationReq.invitationId}`, { role: updateInvitationReq.role });
    return response.data?.data;
});

export const removeInvitation = createAsyncThunk(`${appKey}/removeInvitation`, async (invitationId: string) => {
    const response = await axios.delete(`${usersApiModuleUrl}/invitation/remove/${invitationId}`, { ...globalConfig });
    return response.data?.data;
});

export const resendInvitation = createAsyncThunk(`${appKey}/resendInvitation`, async (invitationId: string) => {
    const response = await axios.get(`${usersApiModuleUrl}/invitation/resend/${invitationId}`, { ...globalConfig });
    return response.data?.data;
});

export const acceptInvitation = createAsyncThunk(`${appKey}/acceptInvitation`, async (acceptInvitationReq: AcceptInvitationRequest) => {
    const body = { ...acceptInvitationReq }; delete body.token
    const response = await axios.put(`${usersApiModuleUrl}/invitation/accept/${acceptInvitationReq.token}`, body);
    return response.data?.data;
});

export const listLocations = createAsyncThunk(`${appKey}/listLocations`, async (data: LocationsFilters) => {
    const params = { ...data };
    if (!params.searchKeyword) delete params.searchKeyword;

    const response = await axios.get(`${apiLocationsUrl}/list`, { ...globalConfig, params });
    return response.data.data; // as ListLocationsResponse
});

export const searchJobLocations = createAsyncThunk(`${appKey}/searchJobLocations`, async (data: LocationsFilters) => {
    const params = { ...data };
    if (!params.searchKeyword) delete params.searchKeyword;
    const response = await axios.get(`${apiLocationsUrl}/list`, { ...globalConfig, params });
    return response.data.data;
});

export const addLocation = createAsyncThunk(`${appKey}/addLocation`, async (locationData: NewLocationRequest) => {
    const response = await axios.post(`${apiLocationsUrl}/create`, locationData);
    return response.data?.data;
});

export const updateLocation = createAsyncThunk(`${appKey}/updateLocation`, async (updateLocationRequest: UpdateLocationRequest) => {
    const response = await axios.put(`${apiLocationsUrl}/update/${updateLocationRequest.locationId}`, { name: updateLocationRequest.name, locationObjectId: updateLocationRequest.locationObjectId });
    return response.data?.data;
});

export const updateLocationsForJob = createAsyncThunk(`${appKey}/updateLocationsForJob`, async (req: UpdateLocationsForJobRequest) => {
    const response = await axios.put(`${apiLocationsUrl}/job/${req.jobId}/update-locations`, { locationIds: req.locationIds, isEnabled: req.isEnabled, numOfQualifiedApplicants: req.numOfQualifiedApplicants });
    return response.data?.data;
});

export const deleteLocation = createAsyncThunk(`${appKey}/deleteLocation`, async (locationIds: string[]) => {
    const response = await axios.delete(`${apiLocationsUrl}/remove`, { data: { locationIds } });
    return response.data?.data;
});



export const defaultUsersFilters: UsersFilters = { skip: 0, limit: ENTERPRISE_USERS_PAGE_LIMIT, page: 1, searchKeyword: '', usersOnly: true };
export const defaultLocationsFilters: LocationsFilters = { skip: 0, limit: ENTERPRISE_LOCATIONS_PAGE_LIMIT, page: 1, searchKeyword: undefined, workplaceTypes: undefined };

export const setIsLoadingUsersList = createAction<boolean>(`${appKey}/setIsLoadingUsersList`);
export const setShowInviteUserModal = createAction<boolean>(`${appKey}/setShowInviteUserModal`);
export const setUsersFilters = createAction<UsersFilters>(`${appKey}/setUsersFilters`);
export const setShowRemoveUserModal = createAction<boolean>(`${appKey}/setShowRemoveUserModal`);
export const setSelectedUserToRemove = createAction<UserItem>(`${appKey}/setSelectedUserToRemove`);
export const setShowJobsConsumedModal = createAction<boolean>(`${appKey}/setShowJobsConsumedModal`);
export const setShowDeleteJobTemplateModal = createAction<boolean>(`${appKey}/setShowDeleteJobTemplateModal`);
export const setSelectedJobTemplateToDelete = createAction<UserItem>(`${appKey}/setSelectedJobTemplateToDelete`);
export const setShowInviteLimitReachedModal = createAction<boolean>(`${appKey}/setShowInviteLimitReachedModal`);
export const setShowReplaceJobContentModal = createAction<boolean>(`${appKey}/setShowReplaceJobContentModal`);
export const setSelectedJobTemplateToReplaceJobContent = createAction<FullTimeJob>(`${appKey}/setSelectedJobTemplateToReplaceJobContent`);
export const setJustResentInvitationTo = createAction<string[]>(`${appKey}/setJustResentInvitationTo`);
export const setShowPostingJobsModal = createAction<boolean>(`${appKey}/setShowPostingJobsModal`);
export const setJobPostsBeingPosted = createAction<PostingJobItem[]>(`${appKey}/setJobPostsBeingPosted`);
export const setShowSimilarJobModal = createAction<boolean>(`${appKey}/setShowSimilarJobModal`);
export const setJobTemplateDetails = createAction<any>(`${appKey}/setJobTemplateDetails`);
export const setSelectedJobTemplatePage = createAction<any>(`${appKey}/setSelectedJobTemplatePage`);
export const setShowOneTimeFreeEditModal = createAction<boolean>(`${appKey}/setShowOneTimeFreeEditModal`);
export const setShowNeedToContactAdminModal = createAction<boolean>(`${appKey}/setShowNeedToContactAdminModal`);
export const setNeedToContactAdminModalContent = createAction<any>(`${appKey}/setNeedToContactAdminModalContent`);
export const setShowUpsertLocationModal = createAction<boolean>(`${appKey}/setShowUpsertLocationModal`);
export const setIsLoadingLocationsList = createAction<boolean>(`${appKey}/setIsLoadingLocationsList`);
export const setLocationsFilters = createAction<LocationsFilters>(`${appKey}/setLocationsFilters`);
export const setSelectedLocation = createAction<LocationInterface>(`${appKey}/setSelectedLocation`);
export const setSelectedLocationsIds = createAction<string[]>(`${appKey}/setSelectedLocationIds`);
export const setShowDeleteLocationModal = createAction<boolean>(`${appKey}/setShowDeleteLocationModal`);
export const setShowAutoCloseLocationModal = createAction<boolean>(`${appKey}/setShowAutoCloseLocationModal`);
export const setShowCloseLocationModal = createAction<boolean>(`${appKey}/setShowCloseLocationModal`);
export const setShowEditJobLocationModal = createAction<boolean>(`${appKey}/setShowEditJobLocationModal`);

const initialState: EnterpriseStoreInferface = {
    isLoadingUsersList: true,
    isLoadingJobTemplatesList: false,
    isLoadingJobTemplatesDetails: false,
    isLoadingInvitingUser: false,
    isLoadingRemovingUser: false,
    isLoadingResendingInvitation: false,
    showInviteUserModal: false,
    showRemoveUserModal: false,
    selectedUserToRemove: null,
    showDeleteJobTemplateModal: false,
    selectedJobTemplateToDelete: null,
    usersFilters: defaultUsersFilters,
    usersCount: null,
    invitationsCount: null,
    users: [],
    invitations: [],
    jobTemplates: [],
    jobTemplateDetails: null,
    showJobsConsumedModal: false,
    showInviteLimitReachedModal: false,
    showReplaceJobContentModal: false,
    selectedJobTemplateToReplaceJobContent: null,
    isLoadingInvitationDetails: true,
    isLoadingUpdatingUser: false,
    isLoadingUpdatingInvitation: false,
    isLoadingRemovingInvitation: false,
    isLoadingAcceptingInvitation: false,
    isLoadingInvitationsList: false,
    invitationDetails: null,
    justResentInvitationTo: [],
    showPostingJobsModal: false,
    jobPostsBeingPosted: [],
    showSimilarJobModal: false,
    jobTemplatesCount: 0,
    selectedJobTemplatePage: 0,
    showOneTimeFreeEditModal: false,
    showNeedToContactAdminModal: false,
    needToContactAdminModalContent: null,
    locationsFilters: defaultLocationsFilters,
    locationsList: [],
    locationsCount: 0,
    physicalBranchesCount: 0,
    remoteLocationsCount: 0,
    showUpsertLocationModal: false,
    showDeleteLocationModal: false,
    selectedLocation: null,
    isLoadingUpsertLocation: false,
    isLoadingLocationsList: false,
    selectedLocationsIds: [],
    showAutoCloseLocationModal: false,
    showCloseLocationModal: false,
    showEditJobLocationModal: false,
}

export const appEnterpriseSlice = createSlice({
    name: appKey,
    initialState,
    reducers: {},
    extraReducers: builder => {
        builder
            // Static 
            .addCase(setIsLoadingUsersList, (state, action) => {
                state.isLoadingUsersList = action.payload;
            })
            .addCase(setShowInviteUserModal, (state, action) => {
                state.showInviteUserModal = action.payload;
            })
            .addCase(setShowJobsConsumedModal, (state, action) => {
                state.showJobsConsumedModal = action.payload;
            })
            .addCase(setJustResentInvitationTo, (state, action) => {
                state.justResentInvitationTo = action.payload;
            })
            .addCase(setUsersFilters, (state, action) => {
                state.usersFilters = action.payload;
            })
            .addCase(setShowRemoveUserModal, (state, action) => {
                state.showRemoveUserModal = action.payload;
            })
            .addCase(setShowNeedToContactAdminModal, (state, action) => {
                state.showNeedToContactAdminModal = action.payload;
            })
            .addCase(setShowInviteLimitReachedModal, (state, action) => {
                state.showInviteLimitReachedModal = action.payload;
            })
            .addCase(setSelectedUserToRemove, (state, action) => {
                state.selectedUserToRemove = action.payload;
            })
            .addCase(setShowDeleteJobTemplateModal, (state, action) => {
                state.showDeleteJobTemplateModal = action.payload;
            })
            .addCase(setShowOneTimeFreeEditModal, (state, action) => {
                state.showOneTimeFreeEditModal = action.payload;
            })
            .addCase(setSelectedJobTemplateToDelete, (state, action) => {
                state.selectedJobTemplateToDelete = action.payload;
            })
            .addCase(setShowReplaceJobContentModal, (state, action) => {
                state.showReplaceJobContentModal = action.payload;
            })
            .addCase(setSelectedJobTemplatePage, (state, action) => {
                state.selectedJobTemplatePage = action.payload;
            })
            .addCase(setNeedToContactAdminModalContent, (state, action) => {
                state.needToContactAdminModalContent = action.payload;
            })
            .addCase(setSelectedJobTemplateToReplaceJobContent, (state, action) => {
                state.selectedJobTemplateToReplaceJobContent = action.payload;
            })
            .addCase(setShowPostingJobsModal, (state, action) => {
                state.showPostingJobsModal = action.payload;
            })
            .addCase(setJobPostsBeingPosted, (state, action) => {
                state.jobPostsBeingPosted = action.payload;
            })
            .addCase(setShowSimilarJobModal, (state, action) => {
                state.showSimilarJobModal = action.payload;
            })
            .addCase(setJobTemplateDetails, (state, action) => {
                state.jobTemplateDetails = action.payload;
            })
            .addCase(setShowDeleteLocationModal, (state, action) => {
                state.showDeleteLocationModal = action.payload;
            })
            .addCase(setShowCloseLocationModal, (state, action) => {
                state.showCloseLocationModal = action.payload;
            })
            .addCase(setShowEditJobLocationModal, (state, action) => {
                state.showEditJobLocationModal = action.payload;
            })
            // API
            .addCase(getJobTemplates.pending, (state, action) => {
                state.isLoadingJobTemplatesList = true;
            })
            .addCase(getJobTemplates.fulfilled, (state, action) => {
                state.isLoadingJobTemplatesList = false;
                state.jobTemplates = action.payload?.templates as any;
                state.jobTemplatesCount = action.payload?.count as any;
            })
            .addCase(getJobTemplates.rejected, (state, action) => {
                state.isLoadingJobTemplatesList = false;
            })
            .addCase(getJobTemplateDetails.pending, (state, action) => {
                state.isLoadingJobTemplatesDetails = true;
            })
            .addCase(getJobTemplateDetails.fulfilled, (state, action) => {
                state.isLoadingJobTemplatesDetails = false;
                state.jobTemplateDetails = action.payload as any;
            })
            .addCase(getJobTemplateDetails.rejected, (state, action) => {
                state.isLoadingJobTemplatesDetails = false;
            })

            .addCase(listUsers.pending, (state, action) => {
                state.isLoadingUsersList = true;
            })
            .addCase(listUsers.fulfilled, (state, action) => {
                state.isLoadingUsersList = false;
                state.users = action.payload?.users;
                state.usersCount = action.payload?.count;
            })
            .addCase(listUsers.rejected, (state, action) => {
                state.isLoadingUsersList = false;
            })
            .addCase(updateUser.pending, (state, action) => {
                state.isLoadingUpdatingUser = true;
            })
            .addCase(updateUser.fulfilled, (state, action) => {
                Toast.Success({ content: t(`user_updated`) });
                state.isLoadingUpdatingUser = false;
            })
            .addCase(updateUser.rejected, (state, action) => {
                Toast.Error({ content: action.error?.message });
                state.isLoadingUpdatingUser = false;
            })
            .addCase(removeUser.pending, (state, action) => {
                state.isLoadingRemovingUser = true;
            })
            .addCase(removeUser.fulfilled, (state, action) => {
                Toast.Success({ content: t(`user_removed`) });
                state.isLoadingRemovingUser = false;
            })
            .addCase(removeUser.rejected, (state, action) => {
                Toast.Error({ content: action.error?.message });
                state.isLoadingRemovingUser = false;
            })
            .addCase(listInvitations.pending, (state, action) => {
                state.isLoadingInvitationsList = true;
            })
            .addCase(listInvitations.fulfilled, (state, action) => {
                state.isLoadingInvitationsList = false;
                state.invitations = action.payload?.invitations;
                state.invitationsCount = action.payload?.count;
            })
            .addCase(listInvitations.rejected, (state, action) => {
                state.isLoadingInvitationsList = false;
            })
            .addCase(sendInvitation.pending, (state, action) => {
                state.isLoadingInvitingUser = true;
            })
            .addCase(sendInvitation.fulfilled, (state, action) => {
                state.isLoadingInvitingUser = false;
            })
            .addCase(sendInvitation.rejected, (state, action) => {
                // Toast.Error({ content: action.error?.message });
                state.isLoadingInvitingUser = false;
            })

            .addCase(getInvitationDetails.pending, (state, action) => {
                state.isLoadingInvitationDetails = true;
            })
            .addCase(getInvitationDetails.fulfilled, (state, action) => {
                state.invitationDetails = action.payload;
                state.isLoadingInvitationDetails = false;
            })
            .addCase(getInvitationDetails.rejected, (state, action) => {
                // Toast.Error({ content: action.error?.message });
                state.isLoadingInvitationDetails = false;
            })

            .addCase(updateInvitation.pending, (state, action) => {
                state.isLoadingUpdatingInvitation = true;
            })
            .addCase(updateInvitation.fulfilled, (state, action) => {
                Toast.Success({ content: t(`invitation_updated`) });
                state.isLoadingUpdatingInvitation = false;
            })
            .addCase(updateInvitation.rejected, (state, action) => {
                Toast.Error({ content: action.error?.message });
                state.isLoadingUpdatingInvitation = false;
            })

            .addCase(removeInvitation.pending, (state, action) => {
                state.isLoadingRemovingInvitation = true;
            })
            .addCase(removeInvitation.fulfilled, (state, action) => {
                Toast.Success({ content: t(`invitation_removed`) });
                state.isLoadingRemovingInvitation = false;
            })
            .addCase(removeInvitation.rejected, (state, action) => {
                Toast.Error({ content: action.error?.message });
                state.isLoadingRemovingInvitation = false;
            })
            .addCase(resendInvitation.pending, (state, action) => {
                state.isLoadingResendingInvitation = true;
            })
            .addCase(resendInvitation.fulfilled, (state, action) => {
                Toast.Success({ content: t(`invitation_resent`) });
                state.isLoadingResendingInvitation = false;
            })
            .addCase(resendInvitation.rejected, (state, action) => {
                Toast.Error({ content: action.error?.message });
                state.isLoadingResendingInvitation = false;
            })
            .addCase(acceptInvitation.pending, (state, action) => {
                state.isLoadingAcceptingInvitation = true;
            })
            .addCase(acceptInvitation.fulfilled, (state, action) => {
                Toast.Success({ content: t(`invitation_accepted`) });
                state.isLoadingAcceptingInvitation = false;
            })
            .addCase(acceptInvitation.rejected, (state, action) => {
                Toast.Error({ content: action.error?.message });
                state.isLoadingAcceptingInvitation = false;
            })
            .addCase(addJobTemplate.pending, (state, action) => {
            })
            .addCase(addJobTemplate.fulfilled, (state, action) => {
                Toast.Success({ content: t(`job_template_added`) });
            })
            .addCase(addJobTemplate.rejected, (state, action) => {
                Toast.Error({ content: action.error?.message });
            })
            .addCase(updateJobTemplate.pending, (state, action) => {
            })
            .addCase(updateJobTemplate.fulfilled, (state, action) => {
                Toast.Success({ content: t(`job_template_modified`) });
            })
            .addCase(updateJobTemplate.rejected, (state, action) => {
                Toast.Error({ content: action.error?.message });
            })
            .addCase(deleteJobTemplate.pending, (state, action) => {
            })
            .addCase(deleteJobTemplate.fulfilled, (state, action) => {
                Toast.Success({ content: t(`job_template_deleted`) });
            })
            .addCase(deleteJobTemplate.rejected, (state, action) => {
                Toast.Error({ content: action.error?.message });
            })
            .addCase(setShowUpsertLocationModal, (state, action) => {
                state.showUpsertLocationModal = action.payload;
            })
            .addCase(setIsLoadingLocationsList, (state, action) => {
                state.isLoadingLocationsList = action.payload;
            })

            .addCase(setSelectedLocation, (state, action) => {
                state.selectedLocation = action.payload;
            })
            .addCase(setSelectedLocationsIds, (state, action) => {
                state.selectedLocationsIds = action.payload;
            })
            .addCase(setLocationsFilters, (state, action) => {
                state.locationsFilters = action.payload;
            })
            .addCase(listLocations.pending, (state, action) => {
                state.isLoadingLocationsList = true;
            })
            .addCase(listLocations.fulfilled, (state, action) => {
                const res: any = action.payload;
                state.isLoadingLocationsList = false;
                state.locationsList = res?.locations;
                state.locationsCount = res?.count;
                state.physicalBranchesCount = res?.physicalCount;
                state.remoteLocationsCount = res?.remoteCount;
            })
            .addCase(listLocations.rejected, (state, action) => {
                state.isLoadingLocationsList = false;
            })
            .addCase(addLocation.pending, (state, action) => {
                state.isLoadingUpsertLocation = true;
            })
            .addCase(addLocation.fulfilled, (state, action) => {
                state.isLoadingUpsertLocation = false;
                Toast.Success({ content: t(`location_added`) });
            })
            .addCase(addLocation.rejected, (state, action) => {
                state.isLoadingUpsertLocation = false;
                const errorMessage = action.error?.message;
                const locationName = action.meta?.arg?.name;
                if (errorMessage === 'Invalid Request') {
                    Toast.Error({ content: t('location_may_already_exists', { value: locationName || '' }) });
                } else {
                    Toast.Error({ content: action.error?.message });
                }
            })
            .addCase(updateLocation.pending, (state, action) => {
                state.isLoadingUpsertLocation = true;
            })
            .addCase(updateLocation.fulfilled, (state, action) => {
                state.isLoadingUpsertLocation = false;
                Toast.Success({ content: t(`location_modified`) });
            })
            .addCase(updateLocation.rejected, (state, action) => {
                state.isLoadingUpsertLocation = false;
                Toast.Error({ content: action.error?.message });
            })
            .addCase(deleteLocation.pending, (state, action) => {
                state.isLoadingUpsertLocation = true;
            })
            .addCase(deleteLocation.fulfilled, (state, action) => {
                state.isLoadingUpsertLocation = false;
                Toast.Success({ content: t(`location_deleted`) });
            })
            .addCase(deleteLocation.rejected, (state, action) => {
                state.isLoadingUpsertLocation = false;
                Toast.Error({ content: action.error?.message });
            })
            .addCase(setShowAutoCloseLocationModal, (state, action) => {
                state.showAutoCloseLocationModal = action.payload;
            })
            .addCase(updateLocationsForJob.pending, (state, action) => {
                // state.isLoadingUpdatingLocationsForJob = true;
            })
            .addCase(updateLocationsForJob.fulfilled, (state, action) => {
                // state.isLoadingUpdatingLocationsForJob = false;
                Toast.Success({ content: t(`locations_updated`) });
            })
            .addCase(updateLocationsForJob.rejected, (state, action) => {
                // state.isLoadingUpdatingLocationsForJob = false;      
                Toast.Error({ content: action.error?.message });
            })
    }
})

export default appEnterpriseSlice.reducer
